import { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  IAccountDetailsRequest,
  IAccountUserResponse,
  IChangeProfileData,
  IEligibleLead,
  IJoinRequestsUserResponse,
  ILeadAssignmentsResponse,
} from './account.interface';

import { ROLE_NAME } from 'enums';
import { axiosApiInstance, axiosInstance } from 'services/config';
import { IUser } from 'types';
import { ICompanyMemberUser } from 'types/domain/companyMember';
import { IJoinRequestsUser } from 'types/domain/joinRequest';
import { IChangeProfileRequests } from 'types/domain/profileUpdate';
import { IUserManagementUser } from 'types/domain/userManagement';
import { convertToCompanyMembersTable, convertToUserManagementTable } from 'utils';
import { convertToJoinRequestsTable } from 'utils/account';

class AccountApi {
  async me(config?: AxiosRequestConfig): Promise<IUser> {
    const response: AxiosResponse<IUser> = await axiosApiInstance.get(
      'account/me',
      config,
    );
    return response.data;
  }

  async getAccount(
    id: number,
    config?: AxiosRequestConfig,
  ): Promise<IAccountUserResponse> {
    const response: AxiosResponse<IAccountUserResponse> = await axiosApiInstance.get(
      `account/${id}`,
      config,
    );
    return response.data;
  }

  async getAllCompanyMembersAccountsByOrganizationId(
    organizationId: number,
  ): Promise<ICompanyMemberUser[]> {
    const response: AxiosResponse<IAccountUserResponse[]> = await axiosApiInstance.get(
      `account/organization/${organizationId}`,
    );
    return convertToCompanyMembersTable(response.data);
  }

  async getAllUserManagementAccountsByOrganizationId(
    organizationId: number,
  ): Promise<IUserManagementUser[]> {
    const response: AxiosResponse<IAccountUserResponse[]> = await axiosApiInstance.get(
      `account/organization/${organizationId}`,
    );
    return convertToUserManagementTable(response.data);
  }

  async getAllJoinRequestsByOrganizationId(
    organizationId: number | undefined,
    config?: AxiosRequestConfig,
  ): Promise<IJoinRequestsUser[]> {
    const response: AxiosResponse<IJoinRequestsUserResponse[]> =
      await axiosApiInstance.get(
        `account/requests/organization/${organizationId}`,
        config,
      );
    return convertToJoinRequestsTable(response.data);
  }

  async approveRequest(
    accountIds: number[],
    config?: AxiosRequestConfig,
  ): Promise<{
    status: boolean;
  }> {
    const isResponseStatus = await axiosApiInstance.post(
      '/account/approve',
      {
        accIdList: accountIds,
      },
      config,
    );

    return isResponseStatus.data;
  }

  async rejectRequest(
    accountIds: number[],
    config?: AxiosRequestConfig,
  ): Promise<{
    status: boolean;
  }> {
    const isResponseStatus = await axiosApiInstance.post(
      '/account/reject',
      {
        accIdList: accountIds,
      },
      config,
    );

    return isResponseStatus.data;
  }

  async changeProfileDetails(
    value: IChangeProfileData,
    token: string | null,
  ): Promise<IChangeProfileRequests> {
    const response: AxiosResponse<IChangeProfileRequests> = await axiosInstance.put(
      `/account`,
      value,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  }

  async deleteProfile(
    id: number | undefined,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const response = await axiosApiInstance.delete(`/account/${id}`, config);
    return response.data;
  }

  async updateAccountDetails(
    accountId: number,
    value: IAccountDetailsRequest,
    config?: AxiosRequestConfig,
  ): Promise<IAccountUserResponse> {
    const response: AxiosResponse<IAccountUserResponse> = await axiosApiInstance.put(
      `/account/details/${accountId}`,
      value,
      config,
    );

    return response.data;
  }

  async getEligibleLeads(organizationId: number): Promise<IEligibleLead[]> {
    const response: AxiosResponse<IAccountUserResponse[]> = await axiosApiInstance.get(
      `account/organization/${organizationId}`,
    );

    return response.data
      .filter(
        account =>
          account.role.name === ROLE_NAME.ADMIN || account.role.name === ROLE_NAME.MENTOR,
      )
      .map(({ id, firstName, lastName, email }) => ({ id, firstName, lastName, email }));
  }

  async getLeadAssignments(
    accountId: number,
    config?: AxiosRequestConfig,
  ): Promise<ILeadAssignmentsResponse> {
    const response: AxiosResponse<ILeadAssignmentsResponse> = await axiosApiInstance.get(
      `/account/${accountId}/lead-assignments`,
      config,
    );

    return response.data;
  }
}

export { AccountApi };
